/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { RiskSpan, RiskSpan$inboundSchema } from "./riskspan.js";

/**
 * Recorded enforcement outcome, independent of policy configuration.
 */
export const EnforcementOutcome = {
  Logged: "logged",
  Denied: "denied",
  Withheld: "withheld",
  WarnedPending: "warned_pending",
  WarnedAcknowledged: "warned_acknowledged",
  WarnedAbandoned: "warned_abandoned",
  Quarantined: "quarantined",
} as const;
/**
 * Recorded enforcement outcome, independent of policy configuration.
 */
export type EnforcementOutcome = ClosedEnum<typeof EnforcementOutcome>;

/**
 * Why the result is suppressed: 'rule' (an exclusion rule, see exclusion_id), 'manual' (dismissed by a user), or 'automated' (the automated false-positive sweep). Null when not suppressed.
 */
export const SuppressedReason = {
  Rule: "rule",
  Manual: "manual",
  Automated: "automated",
} as const;
/**
 * Why the result is suppressed: 'rule' (an exclusion rule, see exclusion_id), 'manual' (dismissed by a user), or 'automated' (the automated false-positive sweep). Null when not suppressed.
 */
export type SuppressedReason = ClosedEnum<typeof SuppressedReason>;

export type RiskResult = {
  /**
   * ID of the durable tool call block recorded for this finding's message, when one exists. Links to the block page at /blocks/:id.
   */
  blockId?: string | undefined;
  /**
   * The chat content part that was scanned, when the finding is anchored to a content part.
   */
  chatContentPartId?: string | undefined;
  /**
   * The chat session containing the message.
   */
  chatId?: string | undefined;
  /**
   * The chat message that was scanned, when the finding is anchored to a message.
   */
  chatMessageId?: string | undefined;
  /**
   * Title of the chat session.
   */
  chatTitle?: string | undefined;
  /**
   * Confidence score for this finding.
   */
  confidence?: number | undefined;
  /**
   * When this result was created.
   */
  createdAt: Date;
  /**
   * Human-readable description of the finding.
   */
  description?: string | undefined;
  /**
   * End byte position within the message content.
   */
  endPos?: number | undefined;
  /**
   * Recorded enforcement outcome, independent of policy configuration.
   */
  enforcementOutcome?: EnforcementOutcome | undefined;
  /**
   * The exclusion rule that suppressed this result. Only set when suppressed_reason is 'rule'.
   */
  exclusionId?: string | undefined;
  /**
   * The mediated MCP execution (tool call, resource read or prompt get) that raised this finding. Filter listResults by it to list that execution's non-dismissed findings.
   */
  executionId?: string | undefined;
  /**
   * Deprecated: mirror of suppressed_at, kept while clients migrate to the suppressed_* fields. Null when not suppressed.
   */
  falsePositiveAt?: Date | undefined;
  /**
   * The result ID.
   */
  id: string;
  /**
   * Whether MCP identity stamped validated principal provenance.
   */
  identityStamped?: boolean | undefined;
  /**
   * The matched secret or sensitive data. Null when the caller isn't authorized to see raw match content for this result's chat (see match_redacted).
   */
  match?: string | undefined;
  /**
   * Opaque fingerprint of match, in the same `<redacted len=N sha=XXXXXXXX>` form as RiskResultRedacted.match_redacted. Populated whenever match is null so callers without raw access still get a stable, non-reversible correlation token. For shadow_mcp findings this is the original match value passed through verbatim (a non-sensitive server URL or command identifier).
   */
  matchRedacted?: string | undefined;
  /**
   * MCP method or equivalent mediated operation.
   */
  mcpMethod?: string | undefined;
  /**
   * Concrete MCP server that executed the operation.
   */
  mcpServerId?: string | undefined;
  /**
   * Concrete mediation surface where the execution was observed.
   */
  mediationSurface?: string | undefined;
  /**
   * Outer gateway that routed the execution, when present.
   */
  metaMcpServerId?: string | undefined;
  /**
   * Which phase of the mediated MCP execution was scanned: its request or its response.
   */
  phase?: string | undefined;
  /**
   * The risk policy ID.
   */
  policyId: string;
  /**
   * Policy version when this result was produced.
   */
  policyVersion: number;
  /**
   * Credential provenance class resolved by MCP identity.
   */
  principalKind?: string | undefined;
  /**
   * The matched rule identifier.
   */
  ruleId?: string | undefined;
  /**
   * Detection source (e.g. gitleaks).
   */
  source: string;
  /**
   * All matched spans attributed to this finding. A finding may carry several correlated spans (e.g. a custom rule matching a tool's function name and its arguments on the same call). The top-level match/start_pos/end_pos mirror the primary (first) span. Null alongside match when the result is redacted.
   */
  spans?: Array<RiskSpan> | undefined;
  /**
   * Start byte position within the message content.
   */
  startPos?: number | undefined;
  /**
   * When this result was suppressed (hidden from open-finding listings). Null when not suppressed.
   */
  suppressedAt?: Date | undefined;
  /**
   * Free-form suppression context: the user-supplied dismissal reason for manual suppressions, the catalog reason for automated ones. Null when absent.
   */
  suppressedDetail?: string | undefined;
  /**
   * Why the result is suppressed: 'rule' (an exclusion rule, see exclusion_id), 'manual' (dismissed by a user), or 'automated' (the automated false-positive sweep). Null when not suppressed.
   */
  suppressedReason?: SuppressedReason | undefined;
  /**
   * Tags from the detection rule.
   */
  tags?: Array<string> | undefined;
  /**
   * Name of the concrete tool, when applicable.
   */
  toolName?: string | undefined;
  /**
   * Toolset serving the execution, when present.
   */
  toolsetId?: string | undefined;
  /**
   * The user who owns the chat session.
   */
  userId?: string | undefined;
};

/** @internal */
export const EnforcementOutcome$inboundSchema: z.ZodMiniEnum<
  typeof EnforcementOutcome
> = z.enum(EnforcementOutcome);

/** @internal */
export const SuppressedReason$inboundSchema: z.ZodMiniEnum<
  typeof SuppressedReason
> = z.enum(SuppressedReason);

/** @internal */
export const RiskResult$inboundSchema: z.ZodMiniType<RiskResult, unknown> = z
  .pipe(
    z.object({
      block_id: z.optional(z.string()),
      chat_content_part_id: z.optional(z.string()),
      chat_id: z.optional(z.string()),
      chat_message_id: z.optional(z.string()),
      chat_title: z.optional(z.string()),
      confidence: z.optional(z.number()),
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      description: z.optional(z.string()),
      end_pos: z.optional(z.int()),
      enforcement_outcome: z.optional(EnforcementOutcome$inboundSchema),
      exclusion_id: z.optional(z.string()),
      execution_id: z.optional(z.string()),
      false_positive_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      id: z.string(),
      identity_stamped: z.optional(z.boolean()),
      match: z.optional(z.string()),
      match_redacted: z.optional(z.string()),
      mcp_method: z.optional(z.string()),
      mcp_server_id: z.optional(z.string()),
      mediation_surface: z.optional(z.string()),
      meta_mcp_server_id: z.optional(z.string()),
      phase: z.optional(z.string()),
      policy_id: z.string(),
      policy_version: z.int(),
      principal_kind: z.optional(z.string()),
      rule_id: z.optional(z.string()),
      source: z.string(),
      spans: z.optional(z.array(RiskSpan$inboundSchema)),
      start_pos: z.optional(z.int()),
      suppressed_at: z.optional(
        z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
      ),
      suppressed_detail: z.optional(z.string()),
      suppressed_reason: z.optional(SuppressedReason$inboundSchema),
      tags: z.optional(z.array(z.string())),
      tool_name: z.optional(z.string()),
      toolset_id: z.optional(z.string()),
      user_id: z.optional(z.string()),
    }),
    z.transform((v) => {
      return remap$(v, {
        "block_id": "blockId",
        "chat_content_part_id": "chatContentPartId",
        "chat_id": "chatId",
        "chat_message_id": "chatMessageId",
        "chat_title": "chatTitle",
        "created_at": "createdAt",
        "end_pos": "endPos",
        "enforcement_outcome": "enforcementOutcome",
        "exclusion_id": "exclusionId",
        "execution_id": "executionId",
        "false_positive_at": "falsePositiveAt",
        "identity_stamped": "identityStamped",
        "match_redacted": "matchRedacted",
        "mcp_method": "mcpMethod",
        "mcp_server_id": "mcpServerId",
        "mediation_surface": "mediationSurface",
        "meta_mcp_server_id": "metaMcpServerId",
        "policy_id": "policyId",
        "policy_version": "policyVersion",
        "principal_kind": "principalKind",
        "rule_id": "ruleId",
        "start_pos": "startPos",
        "suppressed_at": "suppressedAt",
        "suppressed_detail": "suppressedDetail",
        "suppressed_reason": "suppressedReason",
        "tool_name": "toolName",
        "toolset_id": "toolsetId",
        "user_id": "userId",
      });
    }),
  );

export function riskResultFromJSON(
  jsonString: string,
): SafeParseResult<RiskResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskResult' from JSON`,
  );
}
